<script>
import parsePhoneNumber from 'libphonenumber-js';
import {
  getActiveCountryCode,
  getActiveDialCode,
} from 'shared/components/PhoneInput/helper';
import countries from 'shared/constants/countries.js';

export default {
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    dialCode: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: getActiveCountryCode(),
      activeDialCode: getActiveDialCode(),
      phoneNumber: this.value,
    };
  },
  computed: {
    countries() {
      return [
        {
          name: this.dropdownFirstItemName,
          dial_code: '',
          emoji: '',
          id: '',
        },
        ...countries,
      ];
    },
    dropdownFirstItemName() {
      return this.activeCountryCode
        ? this.$t('CONTACTS_PAGE.SEARCH_INPUT_CLEAR_SELECTION')
        : this.$t('CONTACTS_PAGE.SEARCH_INPUT_SELECT_COUNTRY');
    },
    filteredCountriesBySearch() {
      return this.countries.filter(country => {
        const { name, dial_code, id } = country;
        const search = this.searchCountry.toLowerCase();
        return (
          name.toLowerCase().includes(search) ||
          dial_code.toLowerCase().includes(search) ||
          id.toLowerCase().includes(search)
        );
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => country.id === this.activeCountryCode
        );
      }
      return '';
    },
  },
  watch: {
    value() {
      this.syncPhoneState();
    },
    dialCode() {
      this.syncPhoneState();
    },
  },
  mounted() {
    this.syncPhoneState();
  },
  methods: {
    findCountryByDialCode(dialCode) {
      if (!dialCode) {
        return null;
      }

      return countries.find(country => country.dial_code === dialCode) || null;
    },
    findMatchingDialCode(value) {
      if (!value || !value.startsWith('+')) {
        return '';
      }

      return (
        countries
          .map(country => country.dial_code)
          .filter(Boolean)
          .sort((left, right) => right.length - left.length)
          .find(dialCode => value.startsWith(dialCode)) || ''
      );
    },
    setActivePhoneCountry(dialCode, countryCode = '') {
      if (!dialCode) {
        return;
      }

      const matchedCountry = this.findCountryByDialCode(dialCode);
      this.activeDialCode = dialCode;
      this.activeCountryCode =
        countryCode || (matchedCountry && matchedCountry.id) || '';
    },
    syncPhoneState() {
      const rawValue = `${this.value || ''}`.trim();
      const providedDialCode = `${this.dialCode || ''}`.trim();

      if (!rawValue) {
        this.phoneNumber = '';
        if (providedDialCode) {
          this.setActivePhoneCountry(providedDialCode);
        }
        return;
      }

      if (providedDialCode && !rawValue.startsWith('+')) {
        this.setActivePhoneCountry(providedDialCode);
        this.phoneNumber = rawValue;
        return;
      }

      const parsedNumber = rawValue.startsWith('+')
        ? parsePhoneNumber(rawValue)
        : null;

      if (parsedNumber && parsedNumber.countryCallingCode) {
        const parsedDialCode = `+${parsedNumber.countryCallingCode}`;
        this.setActivePhoneCountry(parsedDialCode, parsedNumber.country);
        this.phoneNumber = rawValue.slice(parsedDialCode.length);
        return;
      }

      const matchedDialCode = this.findMatchingDialCode(rawValue);
      if (matchedDialCode && rawValue.length > matchedDialCode.length) {
        this.setActivePhoneCountry(matchedDialCode);
        this.phoneNumber = rawValue.slice(matchedDialCode.length);
        return;
      }

      this.phoneNumber = rawValue;
      if (providedDialCode) {
        this.setActivePhoneCountry(providedDialCode);
      }
    },
    onOutsideClick(e) {
      if (
        this.showDropdown &&
        e.target !== this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },
    onChange(e) {
      this.phoneNumber = e.target.value;
      this.$emit('input', e.target.value, this.activeDialCode);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },
    onSearchCountry() {
      // Reset selected index to 0
      this.selectedIndex = 0;
    },
    moveUp() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.scrollToSelected();
    },
    moveDown() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.filteredCountriesBySearch.length - 1
      );
      this.scrollToSelected();
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const dropdown = this.$refs.dropdown;
        const selectedItem = this.$refs.dropdownItem[this.selectedIndex];
        const dropdownSearchbarHeight = 40;
        if (selectedItem) {
          const selectedItemTop = selectedItem.offsetTop;
          dropdown.scrollTop = selectedItemTop - dropdownSearchbarHeight;
        }
      });
    },
    onSelectCountry(country) {
      if (!country || !this.showDropdown) return;
      this.activeCountryCode = country.id;
      this.searchCountry = '';
      this.activeDialCode = country.dial_code;
      this.$emit('setCode', country.dial_code);
      this.closeDropdown();
      this.$refs.phoneNumberInput.focus();
    },
    toggleCountryDropdown() {
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.searchbar.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
  },
};
</script>

<template>
  <div class="relative phone-input--wrap">
    <div
      class="flex items-center justify-start border border-solid rounded-lg bg-surface"
      :class="
        error
          ? 'border border-solid border-red-400 dark:border-red-400 mb-1'
          : 'mb-4 border-line'
      "
    >
      <div
        class="cursor-pointer py-2 pr-1.5 pl-2 ltr:rounded-l-lg rtl:rounded-r-lg flex items-center justify-center gap-1.5 h-10 w-14 bg-surface-sunken text-muted"
        @click.prevent="toggleCountryDropdown"
      >
        <h5 v-if="activeCountry" class="mb-0">
          {{ activeCountry.emoji }}
        </h5>
        <fluent-icon v-else icon="globe" class="fluent-icon" size="16" />
        <fluent-icon icon="chevron-down" class="fluent-icon" size="12" />
      </div>
      <span
        v-if="activeDialCode"
        class="flex py-2 pl-2 pr-0 text-sm font-normal leading-normal bg-transparent text-muted"
      >
        {{ activeDialCode }}
      </span>
      <input
        ref="phoneNumberInput"
        :value="phoneNumber"
        type="tel"
        class="!mb-0 ltr:!rounded-l-none rtl:!rounded-r-none !border-0 !ring-0 font-normal !w-full !bg-transparent text-sm !px-1.5 placeholder:font-normal"
        :placeholder="placeholder"
        :readonly="readonly"
        :style="styles"
        @input="onChange"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="showDropdown"
      ref="dropdown"
      v-on-clickaway="onOutsideClick"
      tabindex="0"
      class="z-10 absolute h-60 w-[12.5rem] overflow-y-auto top-10 px-0 pt-0 pb-1 border rounded-lg bg-surface-raised border-line"
      @keydown.prevent.up="moveUp"
      @keydown.prevent.down="moveDown"
      @keydown.prevent.enter="
        onSelectCountry(filteredCountriesBySearch[selectedIndex])
      "
    >
      <div class="sticky top-0 p-1 bg-surface-raised">
        <input
          ref="searchbar"
          v-model="searchCountry"
          type="text"
          :placeholder="$t('GENERAL.PHONE_INPUT.PLACEHOLDER')"
          class="!h-8 !mb-0 !text-sm !border !border-solid !border-line"
          @input="onSearchCountry"
        />
      </div>
      <div
        v-for="(country, index) in filteredCountriesBySearch"
        ref="dropdownItem"
        :key="index"
        class="flex items-center px-1 py-0 cursor-pointer h-7 hover:bg-surface-hover"
        :class="{
          'bg-brand-subtle text-brand-text': country.id === activeCountryCode,
          'bg-surface-hover': index === selectedIndex,
        }"
        @click="onSelectCountry(country)"
      >
        <span class="mr-1 text-base">{{ country.emoji }}</span>

        <span
          class="max-w-[7.5rem] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ country.name }}
        </span>
        <span class="ml-1 text-xs text-subtle">{{ country.dial_code }}</span>
      </div>
      <div v-if="filteredCountriesBySearch.length === 0">
        <span class="flex items-center justify-center mt-4 text-sm text-subtle">
          {{ $t('GENERAL.PHONE_INPUT.EMPTY_STATE') }}
        </span>
      </div>
    </div>
  </div>
</template>
